import axios from "axios";
import { GetAllAvailabilityPayload } from "./availabilityReportInterfaces";
import OperationTypes from "../../../types";

export { availabilityReportActions };

/**
 * CR LPP-20500 — timeout (ms) per la ricerca disponibilità by-group. La ricerca per
 * nazione è onerosa; oltre questa soglia il FE interrompe la richiesta (ECONNABORTED)
 * e mostra il popup "richiesta in timeout".
 */
const AVAILABILITY_BY_GROUP_TIMEOUT_MS = 120000;

/**
 * Payload dell'endpoint `POST /sc-be/availability/getAvailabilityByGroup`.
 * Allineato al contract availability service post-CR LPP-20500 (`570c14f`).
 *
 * Tutti i nuovi filtri (`startDate`, `endDate`, `acrissFilter`, `carClassFilter`,
 * `carSegmentFilter`, `marketSegmentFilter`) sono opzionali: omettendoli il
 * comportamento è retro-compatibile (nessun filtro applicato).
 */
export interface IPayloadAvailabilityByGroup {
  pickUpList: {
    displayName?: string;
    locationId?: string;
    locationType?: string;
  }[];
  onlyCustomerAtDesk: boolean;

  // CR LPP-20500 - RF-01: vista temporale estesa (vincolo 180 gg lato FE)
  startDate?: string; // ISO 8601 OffsetDateTime
  endDate?: string; // ISO 8601 OffsetDateTime

  // CR LPP-20500 - RF-04, RF-05: filtri ACRISS / car class / car segment con cascata
  acrissFilter?: string[];
  carClassFilter?: string[];
  carSegmentFilter?: string[];

  // CR LPP-20500 - RF-07: filtro segmento commerciale (multi-select).
  // I valori sono i `code` dei segmenti restituiti da /booking/search/segments.
  // Il BE raggruppa i 4 KPI flusso (issuedRa/effectiveReturns/voidCount/
  // failedCount) per segmento, popolando totalsForSelectedSegments[] +
  // totalsBySegment[]. Drill-down e totalRows restano non filtrati.
  commercialSegmentFilter?: string[];

  // CR LPP-20500 - RF-03: criterio di raggruppamento sub-totali.
  // "REGION" → il BE produce regionalTotalsByRegion[]. Omesso/"NONE" = storico.
  groupBy?: "NONE" | "REGION";

  /**
   * CR LPP-20500 (ottimizzazione ricerca nazione): se false (con groupBy=REGION) la response è
   * leggera (totale + sub-totali per regione con drill-down + elenco DMS per regione), SENZA il
   * dettaglio per-DMS. Il dettaglio dei DMS di una regione si carica on-demand con una seconda
   * chiamata (pickUpList = DMS della regione, groupBy=NONE). Omesso/true = dettaglio completo.
   */
  includeLocationDetail?: boolean;

  /**
   * CR LPP-20500 (drilldown scalabile): quanto drill-down (carSegment → carClass → ACRISS) includere.
   * - "NONE": nessun drill-down (restano aggregatedRows e split A/M, con gli stessi valori);
   * - "TOTAL_ONLY": drill-down solo a livello totale (`totalRowsByGroup`), omesso per-DMS/per-regione;
   * - "FULL" / omesso: drill-down a tutti i livelli (comportamento storico).
   * Il drill-down omesso arriva con `carSegments` null: il FE lo carica a richiesta all'espansione.
   */
  includeDrillDown?: "FULL" | "NONE" | "TOTAL_ONLY";
}

function availabilityReportActions() {
  const baseUrl = ``;

  return {
    getAllData,
    getAllCarSegmentsAndClasses,
    downloadExcel,
    getAllLocationAngGroup,
    getAvailabilityByGroup,
    // CR LPP-20500 - filtri popolamento liste
    getCarSegmentList,
    getCarClassList,
    getAcrissList,
    getMarketSegmentList,
  };

  async function getAllData(
    tenant: string,
    payload: GetAllAvailabilityPayload
  ) {
    return new Promise(function (resolve, reject) {
      axios
        .post(`${baseUrl}/availability/getAvailabilityCarClassOrSegment`, payload, {
          params: { tenant: tenant },
        })
        .then(function (response) {
          resolve(response.data);
        })
        .catch(function (error) {
          reject(error.response);
        });
    });
  }

  async function getAvailabilityByGroup(
    tenant: string,
    payload: IPayloadAvailabilityByGroup
  ) {
    return axios
      .post(`${baseUrl}/availability/getAvailabilityByGroup`, payload, {
        params: {
          tenant: tenant,
        },
        // CR LPP-20500: la ricerca per nazione è pesante. Timeout esplicito così il FE
        // intercetta il timeout (ECONNABORTED) e mostra il popup dedicato invece di
        // restare in caricamento indefinito.
        timeout: AVAILABILITY_BY_GROUP_TIMEOUT_MS,
      })
      .then((response) => response.data);
  }

  async function getAllCarSegmentsAndClasses(tenant: string) {
    return axios
      .get<OperationTypes.CarSegmentCategory[]>(
        `/availability/getAllCarSegmentsAndClasses`,
        { params: { tenant: tenant } }
      )
      .then((response) => response.data);
  }

  async function downloadExcel(
    tenant: string,
    payload: GetAllAvailabilityPayload
  ) {
    return new Promise(function (resolve, reject) {
      axios
        .post(`${baseUrl}/availability/downloadExcelReport`, payload, {
          params: { tenant: tenant },
          responseType: "arraybuffer",
          headers: { "Access-Control-Allow-Origin": true },
        })
        .then(function (response) {
          resolve(response);
        })
        .catch(function (error) {
          reject(error);
        });
    });
  }

  async function getAllLocationAngGroup(tenant: string, locationStatus?: string) {
    return new Promise(function (resolve, reject) {
      const params = {
        tenant: tenant,
        pageSize: 10000,
        pageNumber: 0,
        // CR LPP-20500 RF-02: filtro location status server-side (default BE: ACTIVATED).
        ...(locationStatus ? { locationStatus } : {}),
      };
      axios
        .get(`${baseUrl}/availability/getAllLocationsAndGroups`, {
          params: params,
        })
        .then(function (response) {
          resolve(response.data);
        })
        .catch(function (error) {
          reject(error.response);
        });
    });
  }

  // ─── CR LPP-20500 — Cascata filtri (RF-04, RF-05) ────────────────────────
  // Endpoint MD vehicle, già esposti dal BFF e auth ampliato a
  // `SCOPE_AVAILABILITY_REPORT_MANAGE` / `SCOPE_AVAILABILITY_LINK_OPERATION`
  // (vedi `implementation-log-dario-SC.md` Punto 7).

  async function getCarSegmentList(tenant: string) {
    return axios
      .get(`/masterdatavehicle/carsegment/all`, { params: { tenant } })
      .then((response) => response.data);
  }

  async function getCarClassList(tenant: string) {
    return axios
      .get(`/masterdatavehicle/carclass/all`, { params: { tenant } })
      .then((response) => response.data);
  }

  async function getAcrissList(tenant: string) {
    return axios
      .get(`/masterdatavehicle/acriss/all`, { params: { tenant } })
      .then((response) => response.data);
  }

  // ─── CR LPP-20500 — Filtro segmento di mercato (RF-07, UC-SYS-002) ────────
  // Riusa endpoint booking esistente con auth ampliato lato BFF.

  async function getMarketSegmentList() {
    return axios
      .get(`/booking/search/segments`)
      .then((response) => response.data);
  }
}
